/**
 * Custom React hooks for AI Workflow Decomposition API
 *
 * These hooks provide access to the roleserver's AI workflow decomposition
 * endpoints, enabling task decomposition, workflow execution, and monitoring.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { fetchWorkflowById } from '../api/workflowsApi';
import { useWorkflowSSE } from './useSSE';

/**
 * Hook to decompose a task into an AI workflow graph
 *
 * @param {Object} config - Decomposition configuration
 * @param {string} config.taskDescription - The task to decompose
 * @param {string} config.domain - Optional domain (healthcare, legal, etc.)
 * @param {string} config.company - Optional company context
 * @param {string} config.role - Optional user role
 * @param {Object} config.constraints - Optional constraints (max_cost, max_time, etc.)
 * @param {boolean} config.autoDecompose - Whether to auto-decompose on mount
 * @returns {Object} Decomposition state and methods
 */
export function useAIWorkflowDecompose(config = {}) {
  const [workflow, setWorkflow] = useState(null);
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [decomposing, setDecomposing] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(null);

  const decompose = useCallback(async (customConfig = {}) => {
    const finalConfig = { ...config, ...customConfig };

    if (!finalConfig.taskDescription) {
      setError(new Error('taskDescription is required'));
      return null;
    }

    setDecomposing(true);
    setLoading(true);
    setError(null);
    setPaymentRequired(null);

    try {
      const body = {
        task_description: finalConfig.taskDescription,
        domain: finalConfig.domain,
        company: finalConfig.company,
        role: finalConfig.role,
        constraints: finalConfig.constraints || {},
        use_task_brief: finalConfig.useTaskBrief !== false, // Default to true (Phase 0 enabled)
        use_premium_model_for_analysis: finalConfig.usePremiumModel || false
      };

      // Multi-agent controls (only send non-default values)
      if (finalConfig.defaultMaxRuns != null && finalConfig.defaultMaxRuns > 1) {
        body.default_max_runs = finalConfig.defaultMaxRuns;
      }
      if (finalConfig.defaultQualityThreshold != null) {
        body.default_quality_threshold = finalConfig.defaultQualityThreshold;
      }
      if (finalConfig.maxDynamicDepth != null && finalConfig.maxDynamicDepth > 0) {
        body.max_dynamic_depth = finalConfig.maxDynamicDepth;
      }
      if (finalConfig.maxDynamicWidth != null) {
        body.max_dynamic_width = finalConfig.maxDynamicWidth;
      }

      const response = await axios.post('/api/v1/workflows/decompose', body);

      const data = response.data;

      // Check if response is Phase 0 (task understanding) or direct workflow creation
      if (data.phase === 'task_understanding') {
        // Phase 0: Task brief needs user review
        // Don't set workflow/graph yet - return task brief for review
        return {
          phase: 'task_understanding',
          taskBriefId: data.task_brief_id,
          state: data.state,
          iteration: data.iteration,
          currentIteration: data.current_iteration,
          requiresAction: data.requires_action,
          nextActions: data.next_actions
        };
      } else {
        // Legacy mode or approved brief: workflow created directly
        setWorkflow(data.workflow);
        setGraph(data.graph);
        return data;
      }
    } catch (err) {
      // Handle 402 Payment Required specially
      if (err.response?.status === 402) {
        const paymentData = err.response?.data?.detail;

        // Debug logging
        console.log('402 Payment Required - Full response:', err.response?.data);
        console.log('402 Payment Required - Detail:', paymentData);

        setPaymentRequired({
          workflowId: paymentData?.workflow_id,
          estimatedCost: paymentData?.estimated_cost,
          redirectUrl: paymentData?.redirect_url,
          message: paymentData?.message || 'Insufficient balance to complete workflow'
        });
        setError(new Error(paymentData?.message || 'Payment required'));
        return null;
      }

      // Handle other errors
      const errorMessage = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : err.response?.data?.detail?.message || err.message || 'Decomposition failed';
      setError(new Error(errorMessage));
      return null;
    } finally {
      setDecomposing(false);
      setLoading(false);
    }
  }, [config]);

  // Auto-decompose on mount if configured
  useEffect(() => {
    if (config.autoDecompose && config.taskDescription) {
      decompose();
    }
  }, [config.autoDecompose, config.taskDescription, decompose]);

  return {
    workflow,
    graph,
    loading,
    error,
    decomposing,
    paymentRequired,
    decompose,
    reset: () => {
      setWorkflow(null);
      setGraph(null);
      setError(null);
      setPaymentRequired(null);
    }
  };
}

/**
 * Hook to get workflow status via SSE (real-time) with GET fallback.
 *
 * Replaces the previous setInterval polling with SSE push events.
 * Does one initial GET fetch for full node data, then subscribes to
 * SSE for incremental status/progress updates.
 *
 * Preserves the same return interface for backward compatibility:
 *   { status, loading, error, polling, startPolling, stopPolling, refetch }
 *
 * startPolling/stopPolling now control the SSE subscription rather than
 * an interval timer, but the consumer API is unchanged.
 *
 * @param {string} workflowId - Workflow ID to monitor
 * @param {Object} options - Options (pollInterval is ignored, kept for compat)
 * @param {boolean} options.autoPoll - Auto-start SSE subscription (default: false)
 * @returns {Object} Workflow status and controls
 */
export function useAIWorkflowStatus(workflowId, options = {}) {
  const { autoPoll = false } = options;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscribed, setSubscribed] = useState(autoPoll);

  // SSE subscription — only active when subscribed && workflowId is set
  const activeWorkflowId = subscribed ? workflowId : null;
  const {
    status: sseWorkflowStatus,
    nodes: sseNodes,
    progressPct: sseProgressPct,
    error: sseError,
  } = useWorkflowSSE(activeWorkflowId);

  // Full GET fetch (used for initial load and manual refetch)
  const fetchStatus = useCallback(async () => {
    if (!workflowId) return null;

    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/workflows/${workflowId}/status`);
      setStatus(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : detail?.message || detail?.error || (detail ? JSON.stringify(detail) : err.message);
      setError(new Error(errorMessage));
      return null;
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  // Merge SSE updates into the full status object
  useEffect(() => {
    if (!sseWorkflowStatus && sseNodes.length === 0) return;

    setStatus(prev => {
      if (!prev) {
        // No prior status — do a full fetch to get the rich data
        fetchStatus();
        return prev;
      }

      // Merge SSE node status updates into the existing rich node list
      const updatedNodes = prev.nodes
        ? prev.nodes.map(node => {
            const sseNode = sseNodes.find(n => n.id === node.id);
            if (sseNode) {
              return {
                ...node,
                status: sseNode.status,
                ...(sseNode.cost != null && { cost: sseNode.cost }),
                ...(sseNode.error && { error: sseNode.error }),
              };
            }
            return node;
          })
        : prev.nodes;

      // Recompute progress from merged nodes
      const totalNodes = updatedNodes?.length || 0;
      const completedNodes = updatedNodes?.filter(n => n.status === 'completed').length || 0;
      const failedNodes = updatedNodes?.filter(n => n.status === 'failed').length || 0;
      const runningNodes = updatedNodes?.filter(n => n.status === 'running').length || 0;
      const pendingNodes = updatedNodes?.filter(n => n.status === 'pending').length || 0;
      const waitingApprovalNodes = updatedNodes?.filter(n => n.status === 'waiting_approval').length || 0;

      return {
        ...prev,
        status: sseWorkflowStatus || prev.status,
        nodes: updatedNodes,
        progress: {
          ...prev.progress,
          total_nodes: totalNodes,
          completed_nodes: completedNodes,
          failed_nodes: failedNodes,
          running_nodes: runningNodes,
          pending_nodes: pendingNodes,
          waiting_approval_nodes: waitingApprovalNodes,
          progress_percentage: totalNodes > 0 ? Math.round(completedNodes / totalNodes * 100 * 100) / 100 : 0,
        },
      };
    });
  }, [sseWorkflowStatus, sseNodes, sseProgressPct, fetchStatus]);

  // Propagate SSE errors
  useEffect(() => {
    if (sseError) {
      setError(new Error(sseError));
    }
  }, [sseError]);

  return {
    status,
    setStatus,
    loading,
    error,
    polling: subscribed,
    startPolling: () => setSubscribed(true),
    stopPolling: () => setSubscribed(false),
    refetch: fetchStatus
  };
}

/**
 * Hook to execute a workflow
 *
 * @param {string} workflowId - Workflow ID to execute
 * @returns {Object} Execution controls and state
 */
export function useAIWorkflowExecution(workflowId) {
  const [executing, setExecuting] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);
  // brainstorm-z8t2.24: the grounding gate's 422 detail — { error, message,
  // missing[], input_form_schema }. Held separately from `error` because it is
  // not a failure: the workflow needs data only the user has. Showing a red
  // error banner AND the form would misrepresent what happened.
  const [requiredInputs, setRequiredInputs] = useState(null);

  const clearRequiredInputs = useCallback(() => setRequiredInputs(null), []);

  /**
   * Start execution.
   *
   * @param executionMode step_through | run_until_input | manual
   * @param inputValues   optional { fieldName: value } answering the grounding
   *                      gate (brainstorm-inv1). Sent as the request body;
   *                      roleserver merges it into workflow.input_values and
   *                      seeds it into extracted_data so nodes receive it.
   */
  const execute = useCallback(async (executionMode = 'step_through', inputValues = null) => {
    if (!workflowId) {
      setError(new Error('workflowId is required'));
      return null;
    }

    setExecuting(true);
    setError(null);

    try {
      // CRITICAL FIX: Backend expects execution_mode as query parameter, not body
      const response = await axios.post(
        `/api/v1/workflows/${workflowId}/execute?execution_mode=${executionMode}`,
        inputValues ? { input_values: inputValues } : undefined
      );

      setExecutionResult(response.data);
      setPaused(response.data.status === 'paused');
      setRequiredInputs(null);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;

      // The gate refusing for want of data is a prompt, not an error.
      if (err.response?.status === 422 && detail?.error === 'required_inputs_missing') {
        setRequiredInputs(detail);
        return null;
      }

      const errorMessage = typeof detail === 'string' ? detail : detail?.message || detail?.error || (detail ? JSON.stringify(detail) : err.message);
      setError(new Error(errorMessage));
      return null;
    } finally {
      setExecuting(false);
    }
  }, [workflowId]);

  const resume = useCallback(async () => {
    if (!workflowId) {
      setError(new Error('workflowId is required'));
      return null;
    }

    setExecuting(true);
    setError(null);

    try {
      const response = await axios.post(`/api/v1/workflows/${workflowId}/resume`);
      setExecutionResult(response.data);
      setPaused(response.data.status === 'paused');
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : detail?.message || detail?.error || (detail ? JSON.stringify(detail) : err.message);
      setError(new Error(errorMessage));
      return null;
    } finally {
      setExecuting(false);
    }
  }, [workflowId]);

  const pause = useCallback(async () => {
    if (!workflowId) {
      setError(new Error('workflowId is required'));
      return null;
    }

    try {
      const response = await axios.post(`/api/v1/workflows/${workflowId}/pause`);
      setPaused(true);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : detail?.message || detail?.error || (detail ? JSON.stringify(detail) : err.message);
      setError(new Error(errorMessage));
      return null;
    }
  }, [workflowId]);

  const interrupt = useCallback(async (instructions) => {
    if (!workflowId) {
      setError(new Error('workflowId is required'));
      return null;
    }

    try {
      const response = await axios.post(`/api/v1/workflows/${workflowId}/interrupt`, {
        instructions,
        pause_immediately: true
      });
      setPaused(true);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : detail?.message || detail?.error || (detail ? JSON.stringify(detail) : err.message);
      setError(new Error(errorMessage));
      return null;
    }
  }, [workflowId]);

  return {
    executing,
    paused,
    error,
    executionResult,
    execute,
    resume,
    pause,
    interrupt,
    // brainstorm-z8t2.24
    requiredInputs,
    clearRequiredInputs
  };
}

/**
 * Hook to re-run a task from a completed workflow
 *
 * @param {string} workflowId - Parent workflow ID
 * @param {string} nodeKey - Node key to re-run from
 * @returns {Object} Re-run controls and state
 */
export function useAIWorkflowRerun(workflowId, nodeKey) {
  const [rerunning, setRerunning] = useState(false);
  const [error, setError] = useState(null);
  const [newWorkflow, setNewWorkflow] = useState(null);
  const [suggestions, setSuggestions] = useState(null);

  const rerun = useCallback(async (modifiedTaskDescription = null) => {
    if (!workflowId || !nodeKey) {
      setError(new Error('workflowId and nodeKey are required'));
      return null;
    }

    setRerunning(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/v1/workflows/${workflowId}/nodes/${nodeKey}/rerun`,
        { modified_task_description: modifiedTaskDescription }
      );

      setNewWorkflow(response.data);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : detail?.message || detail?.error || (detail ? JSON.stringify(detail) : err.message);
      setError(new Error(errorMessage));
      return null;
    } finally {
      setRerunning(false);
    }
  }, [workflowId, nodeKey]);

  const getSuggestions = useCallback(async () => {
    if (!workflowId || !nodeKey) {
      setError(new Error('workflowId and nodeKey are required'));
      return null;
    }

    try {
      const response = await axios.get(
        `/api/v1/workflows/${workflowId}/nodes/${nodeKey}/suggestions`
      );

      setSuggestions(response.data);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : detail?.message || detail?.error || (detail ? JSON.stringify(detail) : err.message);
      setError(new Error(errorMessage));
      return null;
    }
  }, [workflowId, nodeKey]);

  return {
    rerunning,
    error,
    newWorkflow,
    suggestions,
    rerun,
    getSuggestions
  };
}

/**
 * Hook to approve a node requiring human approval
 *
 * @param {string} workflowId - Workflow ID
 * @param {string} nodeKey - Node key to approve
 * @returns {Object} Approval controls and state
 */
export function useAIWorkflowApproval(workflowId, nodeKey) {
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);

  const approve = useCallback(async (approved, approvalData = {}) => {
    if (!workflowId || !nodeKey) {
      setError(new Error('workflowId and nodeKey are required'));
      return null;
    }

    setApproving(true);
    setError(null);

    try {
      // Support both old API (notes string) and new API (approvalData object)
      const requestData = typeof approvalData === 'string'
        ? { approved, decision_notes: approvalData }
        : {
            approved,
            decision_notes: approvalData.decision_notes,
            files: approvalData.files,
            input_data: approvalData.input_data
          };

      const response = await axios.post(
        `/api/v1/workflows/${workflowId}/nodes/${nodeKey}/approve`,
        requestData
      );

      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : detail?.message || detail?.error || (detail ? JSON.stringify(detail) : err.message);
      setError(new Error(errorMessage));
      return null;
    } finally {
      setApproving(false);
    }
  }, [workflowId, nodeKey]);

  return {
    approving,
    error,
    approve,
    reject: (notes) => approve(false, typeof notes === 'string' ? { decision_notes: notes } : notes)
  };
}

/**
 * Hook to get workflow cost breakdown
 *
 * @param {string} workflowId - Workflow ID
 * @param {boolean} autoFetch - Auto-fetch on mount
 * @returns {Object} Cost data and controls
 */
export function useAIWorkflowCost(workflowId, autoFetch = false) {
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCost = useCallback(async () => {
    if (!workflowId) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/v1/workflows/${workflowId}/cost`);
      setCostData(response.data);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : detail?.message || detail?.error || (detail ? JSON.stringify(detail) : err.message);
      setError(new Error(errorMessage));
      return null;
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    if (autoFetch && workflowId) {
      fetchCost();
    }
  }, [autoFetch, workflowId, fetchCost]);

  return {
    costData,
    loading,
    error,
    refetch: fetchCost
  };
}

/**
 * Hook to retry a workflow that failed due to payment
 *
 * @param {string} workflowId - Workflow ID to retry
 * @returns {Object} Retry controls and state
 */
export function useAIWorkflowRetry(workflowId) {
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const retry = useCallback(async () => {
    if (!workflowId) {
      setError(new Error('workflowId is required'));
      return null;
    }

    setRetrying(true);
    setError(null);

    try {
      const response = await axios.post(`/api/v1/workflows/${workflowId}/retry`);
      setResult(response.data);
      return response.data;
    } catch (err) {
      // Handle 402 again if still insufficient balance
      if (err.response?.status === 402) {
        const paymentData = err.response?.data?.detail;
        setError({
          isPaymentError: true,
          message: paymentData?.message || 'Payment still required',
          data: paymentData
        });
        return null;
      }

      const errorMessage = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : err.response?.data?.detail?.message || err.message || 'Retry failed';
      setError(new Error(errorMessage));
      return null;
    } finally {
      setRetrying(false);
    }
  }, [workflowId]);

  return {
    retrying,
    error,
    result,
    retry
  };
}

/**
 * Hook to fetch workflow details by ID
 *
 * @param {string} workflowId - Workflow UUID
 * @param {boolean} autoFetch - Whether to auto-fetch on mount
 * @returns {Object} Workflow details state and methods
 */
export function useAIWorkflowDetails(workflowId, autoFetch = false) {
  const [workflow, setWorkflow] = useState(null);
  const [graph, setGraph] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDetails = useCallback(async () => {
    if (!workflowId) {
      setError(new Error('workflowId is required'));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchWorkflowById(workflowId);

      setWorkflow(data.workflow);
      setGraph(data.graph);
      setMetadata({
        total_nodes: data.total_nodes,
        estimated_cost: data.estimated_cost,
        estimated_duration_seconds: data.estimated_duration_seconds,
        risk_analysis: data.risk_analysis
      });

      return data;
    } catch (err) {
      const errorMessage = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : err.response?.data?.detail?.message || err.message || 'Failed to fetch workflow details';
      setError(new Error(errorMessage));
      return null;
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  // Auto-fetch on mount if configured
  useEffect(() => {
    if (autoFetch && workflowId) {
      fetchDetails();
    }
  }, [autoFetch, workflowId, fetchDetails]);

  return {
    workflow,
    graph,
    metadata,
    loading,
    error,
    refetch: fetchDetails
  };
}

/**
 * Hook to transition workflow between states
 *
 * @param {string} workflowId - Workflow UUID
 * @returns {Object} Transition controls and state
 */
export function useAIWorkflowTransition(workflowId) {
  const [transitioning, setTransitioning] = useState(false);
  const [error, setError] = useState(null);

  const transition = useCallback(async (targetStatus) => {
    if (!workflowId) {
      setError(new Error('workflowId is required'));
      return null;
    }

    if (!['draft', 'ready', 'paused', 'abandoned'].includes(targetStatus)) {
      setError(new Error(`Invalid target status: ${targetStatus}`));
      return null;
    }

    setTransitioning(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/v1/workflows/${workflowId}/transition`,
        { target_status: targetStatus }
      );

      return response.data;
    } catch (err) {
      const errorMessage = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : err.response?.data?.detail?.message || err.message || 'Transition failed';
      setError(new Error(errorMessage));
      return null;
    } finally {
      setTransitioning(false);
    }
  }, [workflowId]);

  return {
    transitioning,
    error,
    transition
  };
}
